import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import { Alert, LoadingPanel } from '@gitbook/spine-react';
import { HTTPError, throwIfNotExposable } from '@gitbook/util-debug';

import { apiClient, apiQuery } from '../../state';

interface IntegrationAutoInstallProps {
    /** Organization in which the flow is being done */
    organizationId: string;
    /** Integration to install */
    integration: api.Integration;
    /** ID of the space in which the integration is being installed */
    spaceId?: string;
    /** Content to render when the integration is installed */
    children: React.ReactNode;
}

/**
 * Auto-installs an integration if it is not already installed.
 */
export function IntegrationAutoInstall(props: IntegrationAutoInstallProps) {
    const { organizationId } = props;

    // Get organization to check permissions
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );

    // If permission is installable by anyone, then we auto-install it.
    // Else we follow the normal flow.
    if (organization.permissions.installIntegration) {
        return <InstallIntegrationIfNotInstalled {...props} />;
    }

    return (
        <Alert
            kind="danger"
            title="You cannot install this integration"
            details="You don't have the permission to install this integration."
        />
    );
}

function InstallIntegrationIfNotInstalled(props: IntegrationAutoInstallProps) {
    const { organizationId, spaceId, integration, children } = props;

    const integrationName = integration.name;

    const [state, setState] = React.useState<
        | {
              done: boolean;
              error: null;
          }
        | {
              done: false;
              error: Error;
          }
    >({ done: false, error: null });

    React.useEffect(() => {
        const run = async () => {
            const installationId = getIntegrationInstallationKey(integrationName, {
                organization: organizationId,
            });
            const [orgInstallation, spaceInstallation] = await Promise.all([
                apiClient.integrations
                    .getIntegrationInstallationById(integrationName, installationId)
                    .catch((error: unknown) => {
                        if (error instanceof HTTPError && error.statusCode === 404) {
                            return null;
                        }
                        throw error;
                    }),
                spaceId
                    ? apiClient.integrations
                          .getIntegrationSpaceInstallation(integrationName, installationId, spaceId)
                          .catch((error: unknown) => {
                              if (error instanceof HTTPError && error.statusCode === 404) {
                                  return null;
                              }
                              throw error;
                          })
                    : null,
            ]);

            if (!orgInstallation) {
                await apiClient.integrations.installIntegration(integrationName, {
                    organization: organizationId,
                });

                analytics.logEvent({
                    type: 'integration_install',
                    integration: integration.name,
                    organization: organizationId,
                    withSpaceInstallation: !!spaceId,
                });
            }
            if (spaceId && !spaceInstallation) {
                await apiClient.integrations.installIntegrationOnSpace(
                    integrationName,
                    installationId,
                    { space: spaceId }
                );

                analytics.logEvent({
                    type: 'integration_space_install',
                    integration: integration.name,
                    organization: organizationId,
                    space: spaceId,
                    withAccountInstallation: !!orgInstallation,
                });
            }

            return null;
        };

        run().then(
            () => setState({ done: true, error: null }),
            (error) => {
                setState({ done: false, error });
            }
        );
    }, [integrationName, organizationId, spaceId]);

    if (state.error) {
        throwIfNotExposable(state.error);
        return (
            <Alert
                kind="danger"
                title="Error installing the integration"
                details={state.error.message}
            />
        );
    }

    if (!state.done) {
        return <LoadingPanel size="large" />;
    }

    return children;
}
